import clsx from 'clsx'
import React, { FC, MutableRefObject, useContext } from 'react'

import SvgSadFace from '@Assets/SVG/SadFace'
import SvgWarning from '@Assets/SVG/Warning'
import { Button } from '@Components/Button'
import { NativeModal, NativeModalActions } from '@Components/Modal/NativeModal/NativeModal'
import { ProductContext } from '@Components/Product/Context'
import { SideCartContext } from '@Domains/Cart/Context/SideCartContextProvider'
import { useTranslation } from '@Globals/Translations'

import styles from './ErrorPrompt.module.scss'

export interface ErrorPromptProps {
  code: string
  message: string
  onClose: () => void
  title?: string
}

export const ErrorPrompt: FC<ErrorPromptProps> = ({ code, message, onClose, title }) => {
  const product = useContext(ProductContext)
  const { t, tDanger } = useTranslation()
  const { openCart } = useContext(SideCartContext)

  if (code === 'quantity_not_in_stock') {
    return (
      <div className={clsx(styles.errorPrompt, styles.notInStock)}>
        <SvgSadFace />
        {product?.name && <h1 className={styles.title}>{product?.name}</h1>}
        <span {...tDanger('cart:PP_IS_UNFORTUNATELY_OUT_OF_STOCK_TEXT')} />
        <p>{t('cart:PP_ADJUST_AMOUNT_FROM_CART_TEXT')}</p>
        <span className={styles.buttons}>
          <Button
            className={styles.newButton}
            color="primary"
            onClick={() => {
              openCart()
              onClose()
            }}
          >
            {t('cart:GO_TO_CART')}
          </Button>
          <Button
            className={styles.newButton}
            color="dark"
            onClick={() => { onClose() }}
            variant="outlined"
          >
            {t('cart:PP_CONTINUE_SHOPPING_BUTTON_TEXT')}
          </Button>
        </span>
      </div>
    )
  }

  if (code === 'giftcard_amount_missing') {
    return (
      <div className={clsx(styles.errorPrompt, styles.giftcard)}>
        <SvgWarning />
        <h1 className={styles.title}>{t('giftcard:GIFT_CARD_ERROR_NO_AMOUNT_HEADER')}</h1>
        <p>
          {t(
            'giftcard:GIFT_CARD_ERROR_NO_AMOUNT_CONTENT',
            'Please remember to add an amount so the lucky one receiving a Trendhim gift card feels very special!',
          )}
        </p>
        <Button className={styles.newButton} color="dark" onClick={() => { onClose() }} variant="outlined">{t('common:CLOSE')}</Button>
      </div>
    )
  }
  return (
    <div className={styles.errorPrompt}>
      {title && <h1 className={styles.title}>{title}</h1>}
      <p>{message}</p>
      <span className={styles.buttons}>
        <Button color="dark" onClick={() => { onClose() }} variant="outlined">{t('common:CLOSE')}</Button>
      </span>
    </div>
  )
}

interface ErrorPromptModalProps extends ErrorPromptProps{
  actionRef?: MutableRefObject<NativeModalActions | null>
  backdrop?: boolean
  defaultOpen?: boolean
}

export const ErrorPromptModal: FC<ErrorPromptModalProps> = ({
  actionRef,
  backdrop = true,
  code,
  defaultOpen,
  message,
  onClose,
  title,
}) => (
  <NativeModal
    actionRef={actionRef}
    backdrop={backdrop}
    className={styles.dialog}
    closeOnBackDrop={false}
    closeOnEscape={false}
    defaultOpen={defaultOpen}
    onClose={onClose}
  >
    <ErrorPrompt
      code={code}
      message={message}
      onClose={onClose}
      title={title}
    />
  </NativeModal>
)
